// ─────────────────────────────────────────────────────────────────────────────
// Tiny LCS-based line diff. Produces a side-by-side row plan suitable for
// rendering in two synchronized columns:
//   - "equal"   → render the same line on both sides
//   - "removed" → render text on the LEFT, blank gutter on the right
//   - "added"   → blank gutter on the LEFT, render text on the right
//   - "changed" → both sides have content, both highlighted
//
// Why a hand-rolled diff? jsdiff is great but adds a dependency. The history
// drawer's diff is a 5-30 KB Markdown comparison rendered occasionally — a
// 30-line LCS is plenty fast for that and keeps the bundle lean. If we ever
// need word-level micro-diffs inside changed lines, swap this for jsdiff
// without touching callers.
// ─────────────────────────────────────────────────────────────────────────────

export type DiffOp = 'equal' | 'removed' | 'added';

export interface DiffRow {
  op: DiffOp;
  /** Text shown in the left column (older). Empty string for "added". */
  left: string;
  /** Text shown in the right column (newer). Empty string for "removed". */
  right: string;
  /** 1-based line number on the left. 0 means "no line on this side." */
  leftLineNo: number;
  /** 1-based line number on the right. 0 means "no line on this side." */
  rightLineNo: number;
}

export interface DiffSummary {
  rows: DiffRow[];
  added: number;
  removed: number;
  /** Lines with the SAME text on both sides — "unchanged" count. */
  equal: number;
}

/**
 * Compute a line-level diff. Lines are split on `\n` (CRLF normalized).
 * Empty inputs are tolerated and return all-equal / all-added / all-removed
 * row plans as appropriate.
 */
export function computeLineDiff(left: string, right: string): DiffSummary {
  const a = (left ?? '').replace(/\r\n/g, '\n').split('\n');
  const b = (right ?? '').replace(/\r\n/g, '\n').split('\n');

  const rows: DiffRow[] = [];
  let added = 0;
  let removed = 0;
  let equal = 0;

  // ── Build LCS table (length-only — full traceback below) ───────────────
  // Using a typed array is fast enough for ~10K-line agents (the bodies in
  // practice are 100-500 lines so this is overkill but robust).
  const m = a.length;
  const n = b.length;
  const dp = new Int32Array((m + 1) * (n + 1));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      const cur = i * (n + 1) + j;
      if (a[i] === b[j]) {
        dp[cur] = dp[cur + (n + 1) + 1] + 1; // dp[i+1][j+1] + 1
      } else {
        const down = dp[cur + (n + 1)]; // dp[i+1][j]
        const right = dp[cur + 1]; // dp[i][j+1]
        dp[cur] = down >= right ? down : right;
      }
    }
  }

  // ── Trace back to emit rows ────────────────────────────────────────────
  // Standard LCS traceback — at each step, pick equal / removed / added
  // based on which way the dp table grew.
  let i = 0;
  let j = 0;
  let leftNo = 0;
  let rightNo = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      leftNo += 1;
      rightNo += 1;
      rows.push({
        op: 'equal',
        left: a[i],
        right: b[j],
        leftLineNo: leftNo,
        rightLineNo: rightNo,
      });
      equal += 1;
      i += 1;
      j += 1;
    } else if (dp[(i + 1) * (n + 1) + j] >= dp[i * (n + 1) + (j + 1)]) {
      // Down direction — line at left[i] is "removed."
      leftNo += 1;
      rows.push({
        op: 'removed',
        left: a[i],
        right: '',
        leftLineNo: leftNo,
        rightLineNo: 0,
      });
      removed += 1;
      i += 1;
    } else {
      // Right direction — line at right[j] is "added."
      rightNo += 1;
      rows.push({
        op: 'added',
        left: '',
        right: b[j],
        leftLineNo: 0,
        rightLineNo: rightNo,
      });
      added += 1;
      j += 1;
    }
  }
  while (i < m) {
    leftNo += 1;
    rows.push({
      op: 'removed',
      left: a[i],
      right: '',
      leftLineNo: leftNo,
      rightLineNo: 0,
    });
    removed += 1;
    i += 1;
  }
  while (j < n) {
    rightNo += 1;
    rows.push({
      op: 'added',
      left: '',
      right: b[j],
      leftLineNo: 0,
      rightLineNo: rightNo,
    });
    added += 1;
    j += 1;
  }

  return { rows, added, removed, equal };
}
